/**
 * TicketDashboard v4 — Square KDS + Analytics + History
 *
 * This is the main orchestrator. All logic lives in:
 *   ticketConstants.js  — shared constants
 *   ticketUtils.js      — pure utility functions
 *   firestoreActions.js — Firestore write helpers
 *   hooks/              — custom React hooks
 *   components/         — shared UI components
 *   tabs/               — tab-level page components
 */
import React, { useState, useEffect, useMemo, useRef, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { ALLOWED_ROLES, PRESETS } from './ticketConstants';
import { computeRange, toLocalInput, fsRecordToTicket } from './ticketUtils';
import { useFirestoreTicketsInRange } from './hooks/useFirestoreTicketsInRange';
import { KdsBoardTab } from './tabs/KdsBoardTab';
import { BuffetTab } from './tabs/BuffetTab';
import DailyOps from '../DailyOps/DailyOps';
import { TruncationBanner } from './components/TruncationBanner';
import { LangProvider, useLang, LANGS } from './i18n';
import { useTranslationEngine } from './translationEngine';
// PrepForecast.css stays in the main bundle, ahead of TicketDashboard.css as it
// always was: both define many of the same .pf__ rules and TicketDashboard.css
// must keep winning. Only the tab's JavaScript loads on demand.
import './forecast/PrepForecast.css';
import './TicketDashboard.css';

// Report and planning tabs load the first time they're opened, so the kitchen
// screen doesn't download and parse them to show Live Orders. Live Orders and
// Buffet — used during service, possibly with a weak connection — stay in the
// main bundle.
const TAB_LOADERS = {
  analytics: () => import(/* webpackChunkName: "td-analytics" */ './tabs/AnalyticsTab').then(m => ({ default: m.AnalyticsTab })),
  category:  () => import(/* webpackChunkName: "td-category" */ './tabs/CategorySalesTab').then(m => ({ default: m.CategorySalesTab })),
  history:   () => import(/* webpackChunkName: "td-history" */ './tabs/HistoryTab').then(m => ({ default: m.HistoryTab })),
  tickets:   () => import(/* webpackChunkName: "td-tickets" */ './tabs/TicketsTab').then(m => ({ default: m.TicketsTab })),
  alerts:    () => import(/* webpackChunkName: "td-alerts" */ './tabs/OrderAlertsTab').then(m => ({ default: m.OrderAlertsTab })),
  forecast:  () => import(/* webpackChunkName: "td-forecast" */ './tabs/PrepForecastTab').then(m => ({ default: m.PrepForecastTab })),
  quality:   () => import(/* webpackChunkName: "td-quality" */ './tabs/QualityTab').then(m => ({ default: m.QualityTab })),
};
const lazyTabs = new Map();
/** The lazy component for a tab. A failed load is forgotten, so "Try again" re-imports. */
function lazyTab(key) {
  let Tab = lazyTabs.get(key);
  if (!Tab) {
    Tab = lazy(() => TAB_LOADERS[key]().catch(err => { lazyTabs.delete(key); throw err; }));
    lazyTabs.set(key, Tab);
  }
  return Tab;
}

/**
 * Catches a tab that fails to load or render, so one tab can't take down the
 * whole board. A chunk fails to load when the device is offline, or when the
 * site was redeployed since this page loaded (/tickets is exempt from the
 * update prompt, and the old chunk files are gone) — hence both buttons.
 * "Try again" is done by the parent remounting this boundary under a new key:
 * resetting it in place would re-render the same element, whose failed lazy
 * component rejects again without trying.
 */
class TabBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.error('[TicketDashboard] tab failed:', err); }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

// LangProvider wraps the board (so header/tabs/cards can useLang) and carries the
// shared Firestore auto-translate engine so new ticket/item names get Telugu without
// hand-curation. Language choice is per-device (localStorage 'td_lang').
export default function TicketDashboard() {
  const engine = useTranslationEngine();
  return <LangProvider engine={engine}><TicketDashboardBoard /></LangProvider>;
}

function TicketDashboardBoard() {
  const { t, lang, setLang } = useLang();
  const { role, loading: authLoading } = useAuth();
  const [tab, setTab] = useState('kds');
  const [dopsOpen, setDopsOpen] = useState(false);
  const [preset, setPreset] = useState('today');
  const [csStart, setCsStart] = useState('');
  const [csEnd, setCsEnd] = useState('');
  const [focusMode, setFocusMode] = useState(false);
  // Bumped by a failed tab's "Try again": re-renders here (a failed lazy tab was
  // forgotten, so lazyTab() hands out a fresh one) and remounts the boundary.
  const [tabRetry, setTabRetry] = useState(0);
  const kdsRefetchRef = useRef(null);
  const kdsHelpRef = useRef(null);
  const [headerHelpSent, setHeaderHelpSent] = useState(false);
  const [headerHelpCooldown, setHeaderHelpCooldown] = useState(0);

  // Wire up help state sync from KdsBoardTab
  useEffect(() => {
    if (!kdsHelpRef.current) kdsHelpRef.current = {};
    kdsHelpRef.current._setSent = setHeaderHelpSent;
    kdsHelpRef.current._setCooldown = setHeaderHelpCooldown;
  }, []);

  // iOS/iPadOS: skip native Fullscreen API
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  const toggleFocus = () => {
    setFocusMode(m => {
      const next = !m;
      if (!isIOS) {
        if (next) document.documentElement.requestFullscreen?.().catch(() => {});
        else document.exitFullscreen?.().catch(() => {});
      }
      return next;
    });
  };

  // Keyboard shortcuts: Escape exits focus, F enters it
  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape' && focusMode) { setFocusMode(false); if (!isIOS) document.exitFullscreen?.().catch(() => {}); }
      if ((e.key === 'f' || e.key === 'F') && !e.target.matches('input,textarea') && !focusMode) toggleFocus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMode]);

  // Sync body class for focus mode
  useEffect(() => {
    document.body.classList.toggle('td--focus-mode', focusMode);
    return () => document.body.classList.remove('td--focus-mode');
  }, [focusMode]);

  // Date range
  const { startDt, endDt } = useMemo(() => computeRange(preset, csStart, csEnd), [preset, csStart, csEnd]);

  useEffect(() => {
    if (preset === 'custom' && !csStart && !csEnd) {
      const now = new Date();
      const s = new Date(now); s.setHours(0, 0, 0, 0);
      setCsStart(toLocalInput(s));
      setCsEnd(toLocalInput(now));
    }
  }, [preset, csStart, csEnd]);

  // Historical data for Analytics / All Tickets / Category Sales
  const histEnabled = tab === 'analytics' || tab === 'tickets' || tab === 'category';
  const { records: histRecords, loading: histLoading, error: histError, truncation: histTruncation } =
    useFirestoreTicketsInRange(startDt, endDt, histEnabled);
  const tabTickets = useMemo(() => histRecords.map(fsRecordToTicket), [histRecords]);

  const AnalyticsTab = lazyTab('analytics');
  const CategorySalesTab = lazyTab('category');
  const HistoryTab = lazyTab('history');
  const TicketsTab = lazyTab('tickets');
  const OrderAlertsTab = lazyTab('alerts');
  const PrepForecastTab = lazyTab('forecast');
  const QualityTab = lazyTab('quality');

  // ─── Auth gates ────────────────────────────────────────────────────────────

  if (authLoading) {
    return (
      <div className="td__page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="td__loading">
          <div className="td__spinner" />
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>{t('app.loading')}</p>
        </div>
      </div>
    );
  }

  if (!ALLOWED_ROLES.includes(role)) {
    return (
      <div className="td__page td__access-denied">
        <div className="td__denied-card">
          <span>🔒</span><h2>{t('app.accessTitle')}</h2>
          <p>{t('app.accessBody')}</p>
        </div>
      </div>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={`td__page${focusMode ? ' td__page--focus' : ''}`}>

      {/* Hero header */}
      <header className="td__hero">
        <div className="td__hero-inner">
          <div className="td__hero-left">
            <span className="td__live-badge"><span className="td__live-dot" />{t('app.live')}</span>
            <div>
              <h1 className="td__hero-title">{t('app.title')}</h1>
              <p className="td__hero-sub">{t('app.subtitle')}</p>
            </div>
          </div>
          <div className="td__drp-inline">
            <div className="td__drp-presets">
              {PRESETS.map(p => (
                <button key={p.key} className={`td__drp-btn${preset === p.key ? ' td__drp-btn--on' : ''}`}
                  onClick={() => setPreset(p.key)}>{t(`drp.${p.key}`)}</button>
              ))}
            </div>
            {preset === 'custom' && (
              <div className="td__drp-custom">
                <div className="td__drp-field"><label>{t('drp.from')}</label><input type="datetime-local" value={csStart} onChange={e => setCsStart(e.target.value)} /></div>
                <span className="td__drp-arrow">→</span>
                <div className="td__drp-field"><label>{t('drp.to')}</label><input type="datetime-local" value={csEnd} onChange={e => setCsEnd(e.target.value)} /></div>
              </div>
            )}
          </div>
          <div className="td__hero-actions">
            <div className="td-lang" role="group" aria-label={t('lang.label')}>
              {LANGS.map(l => (
                <button key={l.id} className={`td-lang__btn${lang === l.id ? ' td-lang__btn--on' : ''}`}
                  onClick={() => setLang(l.id)} aria-pressed={lang === l.id} title={l.name}>{l.label}</button>
              ))}
            </div>
            <button className="td__dops-btn" onClick={() => setDopsOpen(true)}
              title={t('app.dailyOpsTitle')}>
              🧺 <span className="td__dops-btn-txt">{t('app.dailyOps')}</span>
            </button>
            {tab === 'kds' && (
              <>
                <button
                  className={`td__focus-btn td__header-help-btn${headerHelpSent ? ' td__header-help-btn--sent' : ''}${headerHelpCooldown > 0 && !headerHelpSent ? ' td__header-help-btn--cooldown' : ''}`}
                  onClick={() => { kdsHelpRef.current?.fn?.(); }}
                  disabled={headerHelpCooldown > 0}
                  title={t('app.needHelp')}>
                  {headerHelpSent ? '✅' : '🆘'}
                </button>
                <button className="td__focus-btn td__refresh-btn" onClick={() => kdsRefetchRef.current?.()} title={t('app.refresh')}>↻</button>
                <button className="td__focus-btn" onClick={toggleFocus} title={t('app.focus')}>⛶</button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Tab navigation */}
      <nav className="td__tabs">
        {[
          { k: 'kds', icon: '🟩', label: 'Live Orders' },
          { k: 'buffet', icon: '🥞', label: 'Buffet' },
          { k: 'alerts', icon: '🚨', label: 'Order Alerts' },
          { k: 'forecast', icon: '🔮', label: 'Prep Forecast' },
          { k: 'category', icon: '🥧', label: 'Category Sales' },
          { k: 'history', icon: '📋', label: 'History' },
          { k: 'quality', icon: '🌟', label: 'Quality' },
          { k: 'tickets', icon: '🎫', label: 'All Tickets' },
          { k: 'analytics', icon: '📊', label: 'Analytics' },
        ].map(tb => (
          <button key={tb.k} className={`td__tab${tab === tb.k ? ' td__tab--on' : ''}`} onClick={() => setTab(tb.k)}>
            {tb.icon} {t(`nav.${tb.k}`)}
          </button>
        ))}
      </nav>

      {/* Tab content */}
      <main className="td__main">
        <TabBoundary key={`${tab}:${tabRetry}`} fallback={(
          <div className="td__error-banner td__tab-error" role="alert">
            <span>⚠️ {t('app.tabFailed')}</span>
            <span className="td__tab-error-actions">
              <button type="button" className="td__tab-error-btn" onClick={() => setTabRetry(n => n + 1)}>{t('app.tabRetry')}</button>
              <button type="button" className="td__tab-error-btn" onClick={() => window.location.reload()}>{t('app.tabReload')}</button>
            </span>
          </div>
        )}>
          <Suspense fallback={<div className="td__loading"><div className="td__spinner" /><p>{t('app.loading')}</p></div>}>
            {tab === 'kds' && <KdsBoardTab focusMode={focusMode} toggleFocus={toggleFocus} refetchRef={kdsRefetchRef} helpRef={kdsHelpRef} onOpenDailyOps={() => setDopsOpen(true)} />}

            {tab === 'analytics' && (
              histLoading ? <div className="td__loading"><div className="td__spinner" /><p>{t('app.loadingOrders')}</p></div>
                : histError ? <div className="td__error-banner">⚠️ {histError}</div>
                  : <>
                    <TruncationBanner info={histTruncation} />
                    <AnalyticsTab tickets={tabTickets} startDt={startDt} endDt={endDt} preset={preset} />
                  </>
            )}

            {tab === 'history' && <HistoryTab startDt={startDt} endDt={endDt} preset={preset} />}

            {tab === 'quality' && <QualityTab startDt={startDt} endDt={endDt} preset={preset} />}

            {tab === 'tickets' && (
              histLoading ? <div className="td__loading"><div className="td__spinner" /><p>{t('app.loadingOrders')}</p></div>
                : histError ? <div className="td__error-banner">⚠️ {histError}</div>
                  : <>
                    <TruncationBanner info={histTruncation} />
                    <TicketsTab tickets={tabTickets} />
                  </>
            )}

            {tab === 'category' && (
              histLoading ? <div className="td__loading"><div className="td__spinner" /><p>{t('app.loadingOrders')}</p></div>
                : histError ? <div className="td__error-banner">⚠️ {histError}</div>
                  : <>
                    <TruncationBanner info={histTruncation} />
                    <CategorySalesTab tickets={tabTickets} />
                  </>
            )}

            {tab === 'alerts' && <OrderAlertsTab startDt={startDt} endDt={endDt} />}

            {tab === 'buffet' && <BuffetTab />}

            {tab === 'forecast' && <PrepForecastTab />}
          </Suspense>
        </TabBoundary>
      </main>

      <DailyOpsDrawer open={dopsOpen} onClose={() => setDopsOpen(false)} />
    </div>
  );
}

/**
 * Slide-over drawer that embeds the full Daily Operations board so kitchen staff
 * can note restock/prep without leaving the ticket board. `<DailyOps />` mounts
 * only while open (its Firestore listeners stay idle when closed) and is kept
 * mounted through the close animation. Content is forced single-column via CSS
 * so it reads well in the narrow panel.
 */
function DailyOpsDrawer({ open, onClose }) {
  const { t } = useLang();
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => setShown(true)); // next frame → slide in
      return () => cancelAnimationFrame(id);
    }
    setShown(false); // slide out; unmount on transitionEnd
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!mounted) return undefined;
    document.body.classList.add('td-dops-lock'); // freeze the board behind it
    return () => document.body.classList.remove('td-dops-lock');
  }, [mounted]);

  if (!mounted) return null;
  // Portal to <body> so the drawer sits above ALL chrome — including the focus-mode
  // Exit/Zoom corner (portaled to body at z-index 10600) and the site nav.
  return createPortal(
    <div className={`td-dops${shown ? ' td-dops--open' : ''}`} role="dialog" aria-modal="true" aria-label={t('drawer.title')}>
      <div className="td-dops__backdrop" onClick={onClose} />
      <aside className="td-dops__panel"
        onTransitionEnd={e => { if (e.target === e.currentTarget && !open) setMounted(false); }}>
        <div className="td-dops__bar">
          <span className="td-dops__title">🧺 {t('drawer.title')}</span>
          <span className="td-dops__spacer" />
          <a className="td-dops__full" href="/daily-ops" title={t('drawer.fullPage')}>↗ {t('drawer.fullPage')}</a>
          <button className="td-dops__close" onClick={onClose} aria-label={t('drawer.close')}>✕</button>
        </div>
        <div className="td-dops__body">
          <DailyOps />
        </div>
      </aside>
    </div>,
    document.body,
  );
}
